/**
 * "Help the developer" panel - merges feedback + roadmap into one floating panel:
 *   left  = compact feedback / feature-request form (same submit as before),
 *   right = terminal-style changelog of recent updates (from src/data/roadmap.ts).
 * Laid out in two columns so it fits without scrolling on desktop.
 */

import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { feedbackRateLimiter } from "@/lib/utils/rateLimiter";
import { t } from "@/lib/i18n";
import { useLocale } from "@/hooks/use-locale";
import { ROADMAP, type RoadmapStatus } from "@/data/roadmap";

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RANK: Record<RoadmapStatus, number> = { shipped: 0, "in-progress": 1, planned: 2, requested: 3 };
const GCOLOR: Record<RoadmapStatus, string> = { shipped: "#22c55e", "in-progress": "hsl(var(--brand-strong))", planned: "#3b82f6", requested: "hsl(var(--muted-foreground))" };
const STAG: Record<RoadmapStatus, { en: string; id: string }> = {
  shipped: { en: "done", id: "selesai" },
  "in-progress": { en: "building", id: "digarap" },
  planned: { en: "planned", id: "rencana" },
  requested: { en: "idea", id: "usulan" },
};

const CHANGELOG = [...ROADMAP]
  .sort((a, b) => (RANK[a.status] - RANK[b.status]) || (b.date ?? "").localeCompare(a.date ?? ""))
  .slice(0, 7);

const FeedbackModal = ({ open, onOpenChange }: FeedbackModalProps) => {
  const { locale } = useLocale();
  const lc: "en" | "id" = locale === "id" ? "id" : "en";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [hoveredStar, setHoveredStar] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !rating || !message) {
      toast({ title: t("fb.missing"), description: t("fb.missingD"), variant: "destructive" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({ title: t("fb.invalidEmail"), description: t("fb.invalidEmailD"), variant: "destructive" });
      return;
    }
    try {
      feedbackRateLimiter.check();
    } catch (rateLimitError: any) {
      toast({ title: t("fb.tooMany"), description: rateLimitError.message, variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const feedbackEmail = import.meta.env.VITE_FEEDBACK_EMAIL || "joyfal2005@gmail.com";
      const response = await fetch(`https://formsubmit.co/ajax/${feedbackEmail}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name, email, rating: `${rating} stars`, message,
          _subject: `Shp.Aid Feedback from ${name}`, _template: "table",
        }),
      });
      if (response.ok) {
        setIsSuccess(true);
        toast({ title: t("fb.sent"), description: t("fb.sentD") });
        setTimeout(() => {
          onOpenChange(false);
          setTimeout(() => { setName(""); setEmail(""); setRating(0); setMessage(""); setIsSuccess(false); }, 300);
        }, 2000);
      } else {
        throw new Error("Failed to send feedback");
      }
    } catch {
      toast({ title: t("fb.failed"), description: t("fb.failedD"), variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>{t("help.title")}</DialogTitle>
          <DialogDescription>{t("help.subtitle")}</DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <CheckCircle2 className="h-14 w-14 text-green-500" />
            <p className="text-lg font-medium">{t("fb.thankYou")}</p>
            <p className="text-sm text-muted-foreground">{t("fb.reviewSoon")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {/* ── Left: compact feedback / request form ── */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="name" className="text-xs">{t("fb.name")} <span className="text-destructive">*</span></Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("fb.namePh")} disabled={isSubmitting} required className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="email" className="text-xs">{t("fb.email")} <span className="text-destructive">*</span></Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" disabled={isSubmitting} required className="h-9" />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">{t("fb.rating")} <span className="text-destructive">*</span></Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} type="button" onClick={() => setRating(star)} onMouseEnter={() => setHoveredStar(star)} onMouseLeave={() => setHoveredStar(0)} disabled={isSubmitting} className="transition-transform hover:scale-110 disabled:opacity-50">
                      <Star className={`h-6 w-6 ${star <= (hoveredStar || rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="message" className="text-xs">{t("fb.message")} <span className="text-destructive">*</span></Label>
                  <span className={`text-[10px] tabular-nums ${message.length > 900 ? "text-destructive" : "text-muted-foreground"}`}>{message.length}/1000</span>
                </div>
                <Textarea id="message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("fb.messagePh")} rows={3} maxLength={1000} disabled={isSubmitting} required />
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("fb.sending")}</>) : t("fb.send")}
              </Button>
            </form>

            {/* ── Right: user-request tracker (theme-aware "terminal") ── */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <p style={{ fontSize: 11.5, fontWeight: 800, color: "hsl(var(--foreground))", marginBottom: 3 }}>{t("help.reqTitle")}</p>
              <p style={{ fontSize: 10.5, lineHeight: 1.45, color: "hsl(var(--muted-foreground))", marginBottom: 8 }}>{t("help.reqNote")}</p>
              <div style={{
                flex: 1, background: "hsl(var(--muted) / 0.4)", border: "1px solid hsl(var(--border))", borderRadius: 10,
                padding: "10px 12px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, lineHeight: 1.95,
              }}>
                {CHANGELOG.map((item) => (
                  <div key={item.id} style={{ display: "flex", gap: 8, alignItems: "baseline", whiteSpace: "nowrap", overflow: "hidden" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, width: 76, flexShrink: 0, color: GCOLOR[item.status], fontWeight: 700 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: GCOLOR[item.status], flexShrink: 0 }} />
                      {STAG[item.status][lc]}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, color: "hsl(var(--foreground))", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title[lc]}</span>
                    {item.date && <span style={{ flexShrink: 0, fontSize: 10, color: "hsl(var(--muted-foreground))" }}>{item.date}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
